import {BaseModel} from '../_base/crud';
import { SPACE_TYPE_NO } from './space.model';
import { TEAM_TYPE_NOTEAM } from './team.model';

/**
 * SUUMO GOのお客様向けのトークンによってログインする無名ユーザー
 */
export const USER_TYPE_ONETIME = 0;
export const USER_TYPE_ADMIN = 1;
export const USER_TYPE_NORMAL = 2;
export const USER_TYPE_OFFSHORE = 3;
export const userTypes = [USER_TYPE_ONETIME, USER_TYPE_ADMIN, USER_TYPE_NORMAL, USER_TYPE_OFFSHORE] as const;
export type UserType = typeof userTypes[number];

export const USER_ROLE_CLIENT_ADMIN = 1;
export const USER_ROLE_CLIENT_NORMAL = 2;
export const USER_ROLE_PRODUCTION_ADMIN = 3;
export const USER_ROLE_PRODUCTION_NORMAL = 4;
export const USER_ROLE_PRODUCTION_OFFSHORE = 5;
export const USER_ROLE_SUUMO_ADMIN = 6;
export const USER_ROLE_SUUMO_NORMAL = 7;
export const USER_ROLE_SUUMO_CUSTOMER = 8;

export const PIC_TYPE_READY_MAID = 1;
export const PIC_TYPE_CUSTOMIZED = 2;

export const userRoles = [
    USER_ROLE_CLIENT_ADMIN,
    USER_ROLE_CLIENT_NORMAL,
    USER_ROLE_PRODUCTION_ADMIN,
    USER_ROLE_PRODUCTION_NORMAL,
    USER_ROLE_PRODUCTION_OFFSHORE,
    USER_ROLE_SUUMO_ADMIN,
    USER_ROLE_SUUMO_NORMAL,
    USER_ROLE_SUUMO_CUSTOMER,
] as const;

export type UserRole = typeof userRoles[number];

/**
 * 無効化されたユーザー
 */
export const USER_STATUS_DISABLE = 0;
/**
 * 有効なユーザー
 */
export const USER_STATUS_ENABLE = 1;
/**
 * 仮パスワードが発行されただけのユーザー
 */
export const USER_STATUS_NOT_CONFIRMED = 2;

export class IconData extends BaseModel {
    pic?: string;
    pic_thumbnail?: string;
    saved_to?: "aws" | "local"; 
}

export class Creator extends IconData {
    id = 0;
    fullname = "";
    teamType = TEAM_TYPE_NOTEAM;
    spaceType = SPACE_TYPE_NO;
    userType = USER_TYPE_ONETIME;
}

export class CreateUserGroupModel extends BaseModel {
    name: string;
}

export class UserGroupWithUsersModel extends BaseModel {
    id = 0;
    name = '';
    users: UserModel[] = [];
}

export class UserGroupWithoutUsersModel extends BaseModel {
    id = 0;
    name = '';
    userIds: number[] = [];
}

export class UserModel extends Creator {
    email: string;
    spaceId?: number;
    spaceName?: string;
    teamId?: number;
    teamName?: string;
    createdAt: string;
    loggedAt: string;
    isDeleted?: boolean;
    accessToken: string;
    refreshToken: string;
    role?: UserRole;
    backGroundPic?: string;
    picType?: number;
    backGroundPicType?: number;
    occupation: string;
    companyName: string;
    phone: string;
    language: Language;
    timeZone: string;
    receiveNotifications?: boolean;
    receiveReports?: boolean;
    notificationSettings: string;
    color: string;
    managedBy?: number;
    managedByUser?: any;
    status: number;
    watchers: UserModel[] = [];
    userGroups: UserGroupWithoutUsersModel[] = [];
    /**
     * ダイレクトチャットにいくつの未読があるか。
     */
    directChatUnreadIds: number[] = [];

    constructor() {
        super();
        this.clear();
    }

    clear(): void {
        this.id = 0;
        this.userType = USER_TYPE_NORMAL;
        this.email = '';
        this.spaceId = undefined;
        this.spaceName = '';
        this.spaceType = 0;
        this.teamId = undefined;
        this.teamName = '';
        this.teamType = 0;
        this.createdAt = '';
        this.loggedAt = '';
        this.isDeleted = undefined;
        this.accessToken = '';
        this.refreshToken = '';
        this.role = undefined;
        this.pic = '/assets/media/users/default.jpg';
        this.backGroundPic = '/assets/media/bg/1.jpg';
        this.picType = undefined;
        this.backGroundPicType = undefined;
        this.fullname = '';
        this.occupation = '';
        this.companyName = '';
        this.phone = '';
        this.language = 'ja';
        this.timeZone = '';
        this.receiveNotifications = undefined;
        this.receiveReports = undefined;
        this.notificationSettings = '';
        this.color = '';
        this.managedBy = undefined;
        this.managedByUser = undefined;
        this.status = 0;
        this.watchers = [];
        this.userGroups = [];
        this.directChatUnreadIds = [];
    }
}

export const isClient = (user: UserModel) => {
    return user.role === USER_ROLE_CLIENT_NORMAL || user.role === USER_ROLE_CLIENT_ADMIN;
};

export const isProduction = (user: UserModel) => {
    return user.role === USER_ROLE_PRODUCTION_ADMIN ||
        user.role === USER_ROLE_PRODUCTION_NORMAL ||
        user.role === USER_ROLE_PRODUCTION_OFFSHORE;
};

export const isSuumo = (user: UserModel) => {
    return user.role === USER_ROLE_SUUMO_NORMAL ||
        user.role === USER_ROLE_SUUMO_ADMIN ||
        user.role === USER_ROLE_SUUMO_CUSTOMER;
};

export const LANGUAGE_MAP = {
    ja: 'ja',
    en: 'en',
    zh: 'zh',
    es: 'es',
    my: 'my',
    vi: 'vi',
} as const;

export type Language = keyof typeof LANGUAGE_MAP;